import React, { useMemo } from 'react';
import { Button as ShadButton } from '@/components/ui/button';
import { Loader2, Save, X, Edit, Trash2, Plus, Grid2x2, Eye, Search, History, Sparkles, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const normalizeIcon = (icon?: string) => icon?.replace(/^icon\s+/, '').trim();

const renderIcon = (icon?: string, className?: string) => {
	switch (normalizeIcon(icon)) {
    case 'ni ni-edit':
      return <Edit className={className} aria-hidden='true' />;
    case 'ni ni-trash':
      return <Trash2 className={className} aria-hidden='true' />;
    case 'ni ni-plus':
      return <Plus className={className} aria-hidden='true' />;
    case 'ni ni-grid-box-alt-fill':
      return <Grid2x2 className={className} aria-hidden='true' />;
    case 'ni ni-eye':
      return <Eye className={className} aria-hidden='true' />;
    case 'ni ni-search':
      return <Search className={className} aria-hidden='true' />;
    case 'ni ni-history':
      return <History className={className} aria-hidden='true' />;
    case 'ni ni-spark-fill':
      return <Sparkles className={className} aria-hidden='true' />;
    case 'ni ni-spark-off-fill':
      return <Sparkles className={className} aria-hidden='true' />;
    case 'ni ni-check-thick':
      return <Check className={className} aria-hidden='true' />;
    default:
      return <em className={cn(normalizeIcon(icon), className)} aria-hidden='true' />;
  }
};

// Map NioBoard theme → shadcn variant
const themeToVariant = (theme?: string): 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' => {
  switch (theme) {
    case 'primary': return 'default';
    case 'danger': return 'destructive';
    case 'secondary': return 'secondary';
    case 'gray': return 'outline';
    case 'info':
    case 'success':
    case 'warning':
    case 'dark': return 'outline';
    default: return 'secondary';
  }
};

// Map NioBoard sizeClass → shadcn size
const toSize = (sizeClass?: string): 'default' | 'sm' | 'lg' | 'icon' => {
  switch (sizeClass) {
    case 'xl':
    case 'lg': return 'lg';
    case 'xs':
    case 'sm': return 'sm';
    default: return 'default';
  }
};

interface IButtonProps {
  onClick: () => void;
  text: string;
  className?: string;
  theme?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'dark' | 'gray';
  preset?: 'default' | 'dim' | 'outline' | 'dimoutline';
  icon?: string;
  iconMode?: 'left' | 'right';
  sizeClass?: 'xl' | 'lg' | 'md' | 'sm' | 'xs';
  type?: 'submit' | 'button' | 'reset';
  textClass?: string;
  isDisabled?: boolean | false;
  isLoading?: boolean | false;
  title?: string;
}

export const Button = (props: IButtonProps) => {
  const { isDisabled, isLoading, text, icon, onClick, type, iconMode, theme, className, preset, sizeClass, title } = props;
  const isIconOnly = !!icon && !text;

  const variant = useMemo(() => {
    if (isIconOnly) return 'outline';
    if (preset === 'outline' || preset === 'dimoutline') return 'outline';
    return themeToVariant(theme);
  }, [isIconOnly, preset, theme]);

  const buttonNode = (
    <ShadButton
      type={type || 'button'}
      variant={variant}
      size={isIconOnly ? 'icon' : toSize(sizeClass)}
      disabled={isDisabled || isLoading}
      onClick={onClick}
      title={title}
      className={cn(
        'font-medium',
        isIconOnly && 'h-9 w-9 p-0 border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary',
        className
      )}
    >
      {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
      {!isLoading && icon && iconMode !== 'right' && renderIcon(icon, cn('h-4 w-4 shrink-0', text ? 'mr-2' : ''))}
      {text}
      {!isLoading && icon && iconMode === 'right' && renderIcon(icon, cn('h-4 w-4 shrink-0', text ? 'ml-2' : ''))}
    </ShadButton>
  );

  return (
    isIconOnly && title ? (
      <TooltipProvider delayDuration={120}>
        <Tooltip>
          <TooltipTrigger asChild>{buttonNode}</TooltipTrigger>
          <TooltipContent>{title}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : buttonNode
  );
};

interface IButtonCancelProps {
  onClick: () => void;
  text?: string;
  theme?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'dark' | 'gray';
  isDisabled?: boolean | false;
  isLoading?: boolean | false;
  icon?: string;
  sizeClass?: 'xl' | 'lg' | 'md' | 'sm' | 'xs';
}

export const ButtonCancel = (props: IButtonCancelProps) => {
  const { isDisabled, isLoading, text, onClick, sizeClass } = props;
  return (
    <ShadButton
      type="button"
      variant="outline"
      size={toSize(sizeClass)}
      disabled={isDisabled || isLoading}
      onClick={onClick}
      className="font-medium"
    >
      <X className="mr-2 h-4 w-4" />
      {text || 'Hủy'}
    </ShadButton>
  );
};

interface IButtonSaveProps {
  onClick: () => void;
  text?: string;
  theme?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'dark' | 'gray';
  preset?: 'default' | 'dim' | 'outline' | 'dimoutline';
  isDisabled?: boolean | false;
  isLoading?: boolean | false;
  icon?: string;
  type?: 'submit' | 'button';
  sizeClass?: 'xl' | 'lg' | 'md' | 'sm' | 'xs';
  className?: string;
}

export const ButtonSave = (props: IButtonSaveProps) => {
  const { type, isDisabled, isLoading, text, onClick, sizeClass, className } = props;
  return (
    <ShadButton
      type={type || 'button'}
      variant="default"
      size={toSize(sizeClass)}
      disabled={isDisabled || isLoading}
      onClick={onClick}
      className={cn('font-medium', className)}
    >
      {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
      {text || 'Lưu'}
    </ShadButton>
  );
};

interface IButtonLinkProps {
  onClick: () => void;
  text?: string;
  className?: string;
  theme?: string;
  icon?: string;
  iconMode?: 'left' | 'right';
  sizeClass?: number;
  type?: 'submit' | 'button' | 'reset';
  textClass?: string;
  isDisabled?: boolean | false;
  title?: string;
}

export const ButtonLink = (props: IButtonLinkProps) => {
  const { isDisabled, text, icon, onClick, type, className, title } = props;
  const iconOnly = !!icon && !text;
  const buttonNode = (
    <ShadButton
      type={type || 'button'}
      variant={iconOnly ? 'outline' : 'ghost'}
      size={iconOnly ? 'icon' : 'sm'}
      disabled={isDisabled}
      onClick={onClick}
      title={title}
      className={cn(
        iconOnly ? 'h-9 w-9 p-0 border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary' : 'h-8 px-2 text-primary hover:text-primary/80',
        className
      )}
    >
      {icon && renderIcon(icon, cn('h-4 w-4', text ? 'mr-1.5' : ''))}
      {text || (!icon ? 'Mở' : '')}
    </ShadButton>
  );

  return (
    iconOnly && title ? (
      <TooltipProvider delayDuration={120}>
        <Tooltip>
          <TooltipTrigger asChild>{buttonNode}</TooltipTrigger>
          <TooltipContent>{title}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : buttonNode
  );
};
